/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useCallback } from 'react';
import styled from 'styled-components';
import StyledProps from '../common/theme/AppThemeInterface';
import appTheme from '../common/theme/appTheme.json';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import {
  SleighIcon,
  SvgAircraftIcon,
  SvgGroundTerminalIcon,
  SvgSpaceVehicleerminalIcon,
} from './TerminalIcons';
import { isOnline, getConnectivityColor } from '../../utils/ConnectionStatusUtils';
import CSS from 'csstype';
import { isChristmasTime } from '../../utils/SeasonalFun';

export const TERMINAL_ICON_CLASSNAME = 'map__terminalicon';
export const TERMINAL_ICON_FIRST_RING_CLASSNAME = 'map__terminalicon--first_ring';
export const TERMINAL_ICON_SECOND_RING_CLASSNAME = 'map__terminalicon--second_ring';
export const TERMINAL_ICON_THIRD_RING_CLASSNAME = 'map__terminalicon--third_ring';

const HCX_BEAM_COLOR = appTheme.overlayBeams.networkGroups.hcx;

export type MarkerStylesType = { [key in TerminalPlatformsEnum]: TerminalIconStaticMarkerStyle };

export interface TerminalIconStaticMarkerProps {
  datapathUuid: string;
  platformType: TerminalPlatformsEnum;
  connectivityStatus: string;
  markerStyles: MarkerStylesType;
  showRing2?: boolean;
  showRing3?: boolean;
  className?: string;
  appConfig?: any;
}

export interface TerminalIconStaticMarkerStyle extends React.SVGProps<SVGSVGElement> {
  innerSvgIconProps: React.SVGProps<SVGSVGElement>;
  divContainerProps: CSS.Properties;
  ring1Props?: React.SVGProps<SVGCircleElement>;
  ring2Props?: React.SVGProps<SVGCircleElement>;
  ring3Props?: React.SVGProps<SVGCircleElement>;
}

const StyledTerminalListIconMarker = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  position: relative;
  z-index: 1;
`;

export const TerminalIconStaticMarker = (props: TerminalIconStaticMarkerProps) => {
  const { datapathUuid, platformType, connectivityStatus, markerStyles, showRing2, showRing3, className, appConfig } = props;

  const markerColor = getConnectivityColor(connectivityStatus);

  let { innerSvgIconProps, divContainerProps, ring1Props, ring2Props, ring3Props, ...wrapperCssStyle } =
    markerStyles[platformType];
  if (showRing2 && isOnline(connectivityStatus)) {
    ring1Props = {
      ...ring1Props,
    };
    ring2Props = {
      ...ring2Props,
      display: 'block',
    };
  }

  if (showRing3 && isOnline(connectivityStatus)) {
    ring2Props = {
      ...ring2Props,
    };
    ring3Props = {
      ...ring3Props,
      display: 'block',
    };
  }

  const getAirborneIcon = useCallback((svgIconProps: React.SVGProps<SVGSVGElement>) => {
    if (isChristmasTime(appConfig)) {
      return SleighIcon(svgIconProps);
    } else {
      return SvgAircraftIcon(svgIconProps);
    }
  }, [appConfig]);

  /**
   * Returns the platform icon related to the given platform type
   *
   * @param platformType platform type based on TerminalPlatformsEnum
   * @param svgIconProps the properties to apply to the SVG icon
   */
  const getPlatformSvgIconByPlatformType = useCallback((
    platformType: TerminalPlatformsEnum,
    svgIconProps: React.SVGProps<SVGSVGElement>,
  ): JSX.Element => {
    switch (platformType) {
      case TerminalPlatformsEnum.Airborne:
        return getAirborneIcon(svgIconProps);
      case TerminalPlatformsEnum.SpaceVehicle:
        return SvgSpaceVehicleerminalIcon(svgIconProps);
      case TerminalPlatformsEnum.Fixed:
      case TerminalPlatformsEnum.Ground:
      case TerminalPlatformsEnum.Maritime:
      case TerminalPlatformsEnum.GroundNomadic:
      default:
        return SvgGroundTerminalIcon(svgIconProps);
    }
  }, [getAirborneIcon]);

  const containerClassName = `${TERMINAL_ICON_CLASSNAME} ${className || ''}`;
  const innerSvgIcon = getPlatformSvgIconByPlatformType(platformType, innerSvgIconProps);

  return (
    <StyledTerminalListIconMarker className={containerClassName} style={{ ...divContainerProps }}>
      <svg viewBox="0 0 42 42" {...wrapperCssStyle} data-test="ftv--terminal-icon">
        <circle
          data-test={`terminal-icon-${datapathUuid}__third-ring`}
          r="19.25"
          cx="50%"
          cy="50%"
          fill="none"
          stroke={HCX_BEAM_COLOR}
          strokeWidth={1.777}
          className={TERMINAL_ICON_THIRD_RING_CLASSNAME}
          {...ring3Props}
        />
        <circle
          data-test={`terminal-icon-${datapathUuid}__second-ring`}
          r="15"
          cx="50%"
          cy="50%"
          fill="none"
          stroke={HCX_BEAM_COLOR}
          strokeWidth={1.777}
          className={TERMINAL_ICON_SECOND_RING_CLASSNAME}
          {...ring2Props}
        />
        <circle
          data-test={`terminal-icon-${datapathUuid}__first-ring`}
          r="11"
          cx="50%"
          cy="50%"
          fill={markerColor}
          stroke="white"
          strokeWidth={0.75}
          className={TERMINAL_ICON_FIRST_RING_CLASSNAME}
          {...ring1Props}
        />
        {innerSvgIcon}
      </svg>
    </StyledTerminalListIconMarker>
  );
};
